/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { apiClient } from '@lib/api-client';
import { useQuery } from '@tanstack/react-query';

// utils
import {
	convertProduction,
	formatDate,
	formatQueryParams,
	parseFcApplicable,
} from '@utils/functions';

// constants
import { NOT_APPLICABLE } from '@config/constants/common';

// types
import {
	TApiResponseGetProductions,
	TProductionPopulated,
} from '../types';

// lodash
import { ROLES } from '@config/constants/roles';
import { TMesurabilityProject } from '@features/offsets/types';
import { TParamsGetApi } from '@global-types/index';
import { useAuthorization } from '@lib/authorization';
import {
	getConvertionFactor,
	getNumericProduction,
	getNumericTokenEquivalent,
	parseTokenEquivalentToTCO2E,
} from '@utils/productions';
import isNil from 'lodash/isNil';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
export type TApiResponse = {
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
	rows: TMesurabilityProject[];
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getProductions = (
	params: TParamsGetApi
): Promise<TApiResponseGetProductions> => {
	const requestParams: Record<string, unknown> = {
		page: params.page,
		pageSize: params.pageSize,
		populate: [
			'project',
			'site',
			'conversionFactor',
			'organization',
			'entity',
			'aco2Token',
			'meterPoint',
			'verifiedBy',
		],
		sort: params.sortParams
			? `${params.sortParams} _id`
			: '_id',
	};

	if (params.queryParams) {
		requestParams.query = formatQueryParams(
			params.queryParams
		);
	}

	return apiClient.get('/productions', {
		params: requestParams,
	});
};

export const getMeasurabilities =
	(): Promise<TApiResponse> => {
		return apiClient.get('/measurabilities', {
			params: {
				fields: ['id'],
			},
		});
	};

const getProductionsForProducer = (
	params: TParamsGetApi
): Promise<TApiResponseGetProductions> => {
	const requestParams: Record<string, unknown> = {
		page: params.page,
		pageSize: params.pageSize,
		populate: [
			'project',
			'site',
			'conversionFactor',
			'organization',
			'entity',
			'meterPoint',
		],
		sort: params.sortParams
			? `${params.sortParams} _id`
			: '_id',
		excludeFields: ['verifiedBy'],
	};

	if (params.queryParams) {
		requestParams.query = formatQueryParams(
			params.queryParams
		);
	}

	return apiClient.get('/productions', {
		params: requestParams,
	});
};

const getProductionsForStaff = (
	params: TParamsGetApi
): Promise<TApiResponseGetProductions> => {
	const requestParams: Record<string, unknown> = {
		page: params.page,
		pageSize: params.pageSize,
		populate: [
			'project',
			'site',
			'conversionFactor',
			'organization',
			'entity',
			'meterPoint',
		],
		sort: params.sortParams
			? `${params.sortParams} _id`
			: '_id',
		excludeFields: ['verifiedBy'],
	};

	if (params.queryParams) {
		requestParams.query = formatQueryParams(
			params.queryParams
		);
	}

	return apiClient.get('/productions', {
		params: requestParams,
	});
};

export const useProductions = (
	page: number,
	size: number,
	queryParams?: Record<string, any>,
	sortParams?: string
) => {
	const { checkRole } = useAuthorization();

	const isProducer = checkRole([
		ROLES.PRODUCER_ADMIN,
		ROLES.PRODUCER_STAFF,
	]);

	const {
		data: measurabilities,
		isFetched: isFetchedMeasurabilities,
	} = useQuery({
		queryKey: ['all-measurabilities'],
		queryFn: getMeasurabilities,
		enabled: isProducer,
		staleTime: 0,
	});

	const idsMeasurabilities = Array.isArray(
		measurabilities?.rows
	)
		? measurabilities?.rows?.map(
				(measurability) => measurability.id
			)
		: [];

	const { data, isFetched, isPending } = useQuery({
		enabled:
			!isProducer ||
			(isProducer &&
				isFetchedMeasurabilities &&
				idsMeasurabilities.length > 0),
		staleTime: 0,
		queryKey: [
			'productions',
			{
				page: page,
				size: size,
				queryParams: queryParams,
				sortParams: sortParams,
			},
		],

		queryFn: () => {
			if (checkRole([ROLES.ADMIN, ROLES.SUPERADMIN])) {
				return getProductions({
					page: page,
					pageSize: size,
					queryParams: queryParams ?? null,
					sortParams: sortParams ?? null,
				});
			} else if (checkRole([ROLES.STAFF])) {
				return getProductionsForStaff({
					page: page,
					pageSize: size,
					queryParams: queryParams ?? null,
					sortParams: sortParams ?? null,
				});
			}
			return getProductionsForProducer({
				page: page,
				pageSize: size,
				queryParams: queryParams ?? null,
				sortParams: sortParams ?? null,
			});
		},
	});

	const updateList = (
		productions: TProductionPopulated[]
	) => {
		return productions.reduce(
			(acc: any, currentItem: TProductionPopulated) => {
				// Fc applicable
				const fcApplicable = parseFcApplicable(
					currentItem.conversionFactorOverride,
					currentItem.conversionFactor,
					currentItem.unit
				);

				const convertionFactor = getConvertionFactor(
					currentItem.conversionFactorOverride,
					currentItem.conversionFactor
				);
				// Mint status
				const { status } = currentItem;
				const blockchain =
					currentItem &&
					currentItem.aco2Token &&
					currentItem.aco2Token.blockchain
						? currentItem.aco2Token.blockchain
						: NOT_APPLICABLE;

				const transactionHash =
					currentItem &&
					currentItem.aco2Token &&
					currentItem.aco2Token.transactionHash
						? currentItem.aco2Token.transactionHash
						: NOT_APPLICABLE;

				const mintStatus = status ?? 'None';

				const modifiedItem = {
					...currentItem,
					blockchain: blockchain,
					transactionHash: transactionHash,
					site: !isNil(currentItem.site)
						? currentItem.site.name
						: NOT_APPLICABLE,
					organization: !isNil(currentItem.organization)
						? currentItem.organization.name
						: NOT_APPLICABLE,
					organizationId: !isNil(currentItem.organization)
						? currentItem.organization.id
						: NOT_APPLICABLE,
					meterPoint: !isNil(currentItem.meterPoint)
						? currentItem.meterPoint.serialNumber
						: NOT_APPLICABLE,

					entity: !isNil(currentItem.entity)
						? currentItem.entity.name
						: NOT_APPLICABLE,
					proof: !isNil(currentItem.proof)
						? currentItem.proof
						: NOT_APPLICABLE,
					projectId: !isNil(currentItem.project)
						? currentItem.project.id
						: NOT_APPLICABLE,
					siteId: !isNil(currentItem.site)
						? currentItem.site.id
						: NOT_APPLICABLE,
					project: !isNil(currentItem.project)
						? currentItem.project.name
						: NOT_APPLICABLE,
					startDate: !isNil(currentItem.startDate)
						? formatDate(
								currentItem.startDate,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					endDate: !isNil(currentItem.endDate)
						? formatDate(
								currentItem.endDate,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					createdAt: !isNil(currentItem.createdAt)
						? formatDate(
								currentItem.createdAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,

					production: convertProduction(
						currentItem.quantity,
						currentItem.unit
					),
					productionNumeric: getNumericProduction(
						currentItem.quantity,
						currentItem.unit
					),
					tokenEquivalentNumeric:
						getNumericTokenEquivalent(
							convertionFactor,
							currentItem.quantity
						),
					tokenEquivalent: parseTokenEquivalentToTCO2E(
						convertionFactor,
						currentItem.quantity
					),
					fcApplicable: fcApplicable,
					mintStatus: mintStatus,
					verifiedBy:
						!isNil(currentItem.verified) &&
						!isNil(currentItem.verifiedBy)
							? `${currentItem.verifiedBy.firstName} ${currentItem.verifiedBy.lastName}`
							: NOT_APPLICABLE,
				};

				acc.push(modifiedItem);

				return acc;
			},
			[]
		);
	};

	return {
		data: !isProducer
			? data?.rows && Array.isArray(data?.rows)
				? updateList(data?.rows)
				: []
			: data?.rows &&
				  Array.isArray(data?.rows) &&
				  idsMeasurabilities.length > 0
				? updateList(data?.rows)
				: [],

		isLoading:
			!isProducer ||
			(isProducer &&
				isFetchedMeasurabilities &&
				idsMeasurabilities.length > 0)
				? isPending
				: false,
		isFetched,
		isPending,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
